/**
 * How a trail lies on the ground.
 *
 * The simulation records where people walked as a polyline of ~64 m samples
 * (`BandSystem.paths()`); this turns that record into the line the eye sees.
 * It is **render-only** — nothing under `src/sim/` calls it, the recorded
 * points are untouched, and a band still walks the route it always walked.
 * That is what makes the shape of a way free to change.
 *
 * ## What was here before, and why it read as drawn-on
 *
 * `main.ts` interpolated **linearly between consecutive recorded points** and
 * seated each sample on `surfaceHeight`. That gives a line which follows the
 * ground *vertically* and is perfectly straight *in plan*: measured across all
 * 33 shipped ways, sinuosity is **1.0000 at p10, p50, p90 and max** — every
 * way in the world is a ruled line. Real paths are not, and the eye knows it
 * at a glance: a straight line over a landscape reads as something laid on top
 * of the world rather than worn into it.
 *
 * The recorded line is straight because the walker is: `sim/mortals/band.ts`
 * interpolates its trace between two points and `sim/mortals/paths.ts` keeps
 * whatever it sampled. `tools/ways.ts` measures the recorded polylines at
 * sinuosity **1.0000 across all 33** as well, so nothing downstream of the
 * simulation was losing the shape — there was never any shape.
 *
 * The 20 m resample was also too coarse for the ground it is trying to follow.
 * `detailHeight`'s band limit stops at `2 · LATTICE = 8 m`, and the finest
 * group that survives it is the 26-frequency one at a **10.0 m wavelength**
 * (`region.cell / 26`), with the 8.5 group's second octave at 15.1 m under it.
 * A 20 m step samples a 10 m wave at a quarter of the rate it needs and cuts
 * the corner off every hummock. `tools/ways.ts --steps` measures what that
 * costs and what buys it back; the numbers are on `ROUTE.step` below.
 *
 * ## The contract
 *
 * `routeFor` takes the recorded line and returns the drawn line. It may
 * **reshape freely in plan and in sampling density**, and it must obey three
 * rules, all of which exist because something downstream depends on them:
 *
 *  1. **Pure.** A function of its arguments and of the world's own fields.
 *     No clock, no camera, no visit order, no `Math.random` — two clients
 *     drawing the same way must draw the same line (law 1's spirit, applied to
 *     a render-only path so that a shared world looks shared).
 *  2. **Endpoints are anchored.** The first and last recorded points are where
 *     the way actually meets its camp or its neighbour. Everything between
 *     them may move; those two may not.
 *  3. **Bounded excursion.** A route that wanders far from the recorded line is
 *     no longer that way. The deviation cap is what keeps "curves around the
 *     tree" from becoming "goes somewhere else". Every drawn point is within
 *     `maxDeviation` metres of the recorded polyline, **for any shape of
 *     record** — see `pullInside`, which is where that is made true rather
 *     than hoped, and `tools/ways.ts --bent`, which is where it is checked
 *     against a record that actually bends. The shipped record does not bend
 *     (sinuosity 1.0000 at max across all 33), so nothing measured on the
 *     shipped world can tell a clamp that holds from one that does not, and
 *     for a while this one did not.
 *
 * ## What replaces the ruled line
 *
 * Not a search. A **relaxation**: the recorded line is resampled onto control
 * points, every interior control point is pushed by the negative gradient of a
 * cost the world itself defines, and the settled polygon is corner-cut and
 * re-seated. Four terms, and each one is a thing a person walking would care
 * about:
 *
 * - **Length.** Nobody walks further than they have to. This is the tension
 *   that keeps a detour honest: a bend has to buy its own metres.
 * - **Grade, squared.** A segment costs `L + CLIMB·Δh²/L`. The square is the
 *   whole model: `Σ|Δh|` — total ascent — has *no gradient at all* on a
 *   monotone climb (move a vertex along a uniform hillside and the sum does
 *   not change), so a linear climb cost cannot bend anything. The squared
 *   grade can: its force on a vertex is `2·CLIMB·∇h·(g_next − g_prev)`, which
 *   is zero exactly when the two neighbouring grades are equal. **A quadratic
 *   grade penalty is a constant-grade traverse, written as a force**, and a
 *   constant-grade traverse is what a footpath on a hillside is.
 * - **Wet ground.** Away from accumulated flow, so a way skirts the boggy
 *   hollow and crosses the stream rather than walking down it.
 * - **Standing timber.** Away from closed canopy, toward the glade. This is
 *   the "curves around trees" term, and it is **optional** — it arrives as a
 *   sampler in `RouteOptions.fields`, and a caller that has no canopy field
 *   gets a route shaped by ground and water alone rather than an error.
 *
 * Then a tether to the recorded position, quadratic, plus a hard clamp: those
 * two are rule 3, and the endpoints are simply never moved, which is rule 2.
 * The tether is a shaper and the clamp is the guarantee — `--tether` measures
 * p50 sinuosity moving only 1.0178 → 1.0242 across the whole range 0.35 → 0,
 * so it was never what was holding the line in.
 *
 * The whole thing is Jacobi — every force is read from one snapshot of the
 * line and applied afterwards — so the answer does not depend on the order the
 * points are visited, which is the cheapest possible form of law 1.
 *
 * ## What this deliberately does not do, and what would be needed
 *
 * **It does not switchback.** Gradient descent from the recorded line finds the
 * nearest local minimum, and a zigzag up a fall line is a different basin
 * entirely — no amount of `climb` reaches it, it would need a search. That is
 * the right trade for now: a way that switchbacked would no longer be within
 * rule 3's excursion cap of the line the band actually walked.
 *
 * **It bends around WOODS, not around individual trees.** The canopy term
 * reads `standField`, the ~1200 m / ~300 m pair that decides where a stand is
 * closed and where its glades are; the trees themselves are drawn from a
 * per-chunk rng stream inside `buildVegetation` and there is no per-position
 * function that returns them. That is why the measurement is taken the other
 * way round: `tools/ways.ts` builds the LOD-0 vegetation of every chunk a way
 * crosses and counts the crowns the finished line passes under, so the claim
 * is checked against the trees that are really there (11.19 → 9.94 per km, and
 * boles walked through 1.31 → 0.97). Those two are read through `buildChunk`
 * and therefore move whenever the vegetation does: re-run `tools/ways.ts` after
 * any change to `world/chunk.ts` rather than quoting them.
 *
 * Individual avoidance needs an obstacle query the world does not currently
 * expose; when it does, it arrives as one more sampler on `RouteFields` and
 * nothing else here changes.
 */
import { fbm, clamp, smoothstep } from '@/core/rng';

/** A point as the simulation records it: plan position only. */
export interface RoutePoint { x: number; z: number }

/**
 * A point as the renderer wants it: seated on the drawn surface.
 *
 * `cross` is the ground's slope ACROSS the way at this point — `dy/ds` along
 * the ribbon's own sideways axis, measured at `ROUTE.crossEps`. It exists
 * because `render/paths.ts` gives both of a rung's vertices the centreline's
 * `y`: a 7.6 m road therefore lies horizontally across its own width, and on a
 * cross-slope one edge is buried and the other floats. It is optional so that
 * the field can travel to the ribbon through `PathView.points` — which is
 * typed `{x, y, z}` — without any change in `main.ts`.
 */
export interface SeatedPoint { x: number; y: number; z: number; cross?: number }

/**
 * The world's own fields, as samplers. Passed in rather than imported so this
 * module stays free of the region and can be driven from a probe — and every
 * one is optional, so a caller that has not wired a field yet loses that term
 * and nothing else.
 */
export interface RouteFields {
  /**
   * How closed the stand is here, 0..1 — `chunk.ts`'s `standField`. This is
   * the term that bends a way around a wood.
   */
  canopy?(x: number, z: number): number;
  /**
   * Accumulated flow, raw, exactly as `region.flowAt` returns it. Normalised
   * inside this module (see `WET_LO` / `WET_HI`) so the caller passes the
   * world's number and not a taste.
   */
  flow?(x: number, z: number): number;
}

export interface RouteOptions {
  /**
   * A CAP on the metres between ground samples along the finished line — it can
   * only ever make the drawn line finer than `ROUTE.step`, never coarser.
   *
   * That asymmetry is deliberate and it is a safety rail, not a preference. The
   * sampling `main.ts` was passing before this lane was a 20 m constant of its
   * own, and 20 m is measurably too coarse for a line that now bends: routed
   * with the shape terms on and still chorded at 20 m — the `20 m` row of
   * `tools/ways.ts --steps` — **6.236% of the drawn network is cut through by
   * the ground** at 300 m, worse than the ruled line's 4.676% (the default
   * run's BEFORE row), because a bending line crosses more relief per metre.
   * *(`--unwired` is NOT the run that shows this, and an earlier version of
   * this note cited it: `--unwired` passes `step` through `RouteOptions`,
   * where this very rail turns the 20 back into 8 and the line comes out at
   * 0.024%. The rail works; the citation was wrong.)*
   *
   * A caller left holding a stale constant must not be able to make the ribbon
   * worse than it was; asking for MORE samples than the measurement requires is
   * harmless, so that direction stays open.
   *
   * A probe that genuinely needs a coarser line — `--steps` sweeps up to 20 m —
   * sets `tune.step`, which is the override with no rail on it.
   */
  step?: number;
  /**
   * How far, in metres, the drawn line may stray from the recorded one.
   * Defaults to `ROUTE.maxDeviation`; `Infinity` means "no cap", which is only
   * ever correct for a probe measuring the unconstrained shape.
   */
  maxDeviation?: number;
  /** The world's fields. Omit and the route is shaped by the ground alone. */
  fields?: RouteFields;
  /**
   * Override the measured constants. For `tools/ways.ts`, which must sweep the
   * REAL function rather than a copy of it that drifts from it — the same
   * reason `chunk.ts` exposes `BenchTune`. The shipped renderer never passes
   * this, so what ships is `ROUTE` and only `ROUTE`.
   */
  tune?: Partial<RouteTune>;
}

/** Every knob the relaxation has, in one object so a probe can sweep them. */
export interface RouteTune {
  /**
   * Metres between the control points the relaxation actually moves — the
   * finest bend the route can express, so a scale choice and not a taste one.
   *
   * `tools/ways.ts --control`, over all 33 ways:
   *
   * | control | sinuosity p50 | ascent | build |
   * |---|---|---|---|
   * | 64 m | 1.0095 | 2,807 m | 142 ms |
   * | 48 m | 1.0117 | 2,790 m | 164 ms |
   * | 32 m | 1.0182 | 2,782 m | 236 ms |
   * | **24 m** | **1.0235** | **2,772 m** | **305 ms** |
   * | 16 m | 1.0309 | 2,760 m | 465 ms |
   *
   * The build column is the one thing here that is a wall-clock time and not a
   * property of the world: it is this machine, and it drifts a few percent
   * between runs. The ratios are the load-bearing part.
   *
   * Shape keeps coming down to 16 m and so does cost, near enough linearly, so
   * there is no knee to read: this is a budget cut, and it is where it is
   * because the whole network is re-routed on every `pathsVersion` change. 16 m
   * is the value to take if that ever becomes incremental.
   */
  control: number;
  /**
   * Relaxation sweeps. `tools/ways.ts --iterations`, at `settle` 0.97:
   *
   * | sweeps | 5 | 10 | 20 | 40 | 80 | 160 |
   * |---|---|---|---|---|---|---|
   * | total ascent | 2,805 | 2,794 | 2,779 | 2,770 | 2,766 | 2,762 m |
   * | mean shift from the row above | — | 5.74 | 6.68 | 5.43 | 3.41 | 1.24 m |
   * | p50 sinuosity | 1.0078 | 1.0162 | 1.0231 | 1.0272 | 1.0297 | 1.0299 |
   *
   * **Ascent — the thing being minimised — is converged; shape is not.** At the
   * shipped 24 sweeps ascent is 2,772 m, within 0.4% of the 160-sweep answer
   * for 15% of its cost, and the fall is monotone. p50 sinuosity at 24 is
   * 1.0235 against 1.0299 at 160, so the drawn line has four fifths of the bend
   * the relaxation would eventually find and the last fifth costs 6.7× the
   * budget. That is the same trade `control` makes and it is made the same way:
   * this is a budget cut, taken because the whole network is re-routed on every
   * `pathsVersion` change. Cost is linear in sweeps — ~12 ms per sweep for the
   * whole 41.3 km network on this machine.
   */
  iterations: number;
  /**
   * Metres of movement per unit of force, on the first sweep. Force is
   * dimensionless (the gradient of a cost measured in metres, with respect to
   * metres), so this is the whole step size. Capped per sweep at
   * `control · 0.25`, which is what keeps a steep segment's inverted tension —
   * `1 − CLIMB·g²` goes negative on purpose, because a longer gentler way IS
   * cheaper — from running away between the tether and the clamp.
   */
  rate: number;
  /**
   * What the step is multiplied by each sweep. Below 1 the total displacement
   * budget is `rate/(1 − settle)` metres of force-following, whatever
   * `iterations` is; at exactly 1 the relaxation orbits and never lands.
   */
  settle: number;
  /**
   * Weight on `Δh²/L`: a segment at grade `g` costs `L·(1 + climb·g²)`.
   *
   * **Calibrated, not chosen.** `tools/ways.ts --tobler` fits this quadratic to
   * Tobler's hiking function — `W = 6·exp(−3.5·|g + 0.05|)` km/h, whose
   * slowdown against the flat is `exp(3.5·(|g + 0.05| − 0.05))` — over the
   * 2,355 segment grades the shipped ways actually carry. Three estimators,
   * because least squares on `1 + C·g²` weights each sample by g⁴ and is
   * therefore decided by the steepest few percent, which is ground a way
   * crosses and no walker chooses:
   *
   * | estimator | C |
   * |---|---|
   * | least squares, every segment | 18.2 |
   * | least squares, \|g\| ≤ p90 = 24.9% | 21.4 |
   * | matched on the network's TOTAL climb cost | 19.5 |
   *
   * 22 is the round of the top of that bracket. At 22 a 1-in-4 costs 2.4× its
   * own length, which is exactly what Tobler charges for a 25% grade.
   *
   * **The temptation here is to raise it, and it must be refused.** `--climb`
   * measures 64 as a visibly wigglier network — p50 sinuosity 1.0235 → 1.0543,
   * grade p90 21.9% → 19.5% — and it is three times what every estimator says
   * a walker charges for a hill. The extra shape would be this file inventing
   * a hillside the world does not have, which is the one thing it must not do.
   */
  climb: number;
  /**
   * Pull back toward the recorded position, quadratic — rule 3's soft half.
   *
   * Weak on purpose. `tools/ways.ts --tether`, across the whole range:
   *
   * | tether | 0.35 | 0.15 | 0.06 | **0.03** | 0.015 | 0.006 | 0 |
   * |---|---|---|---|---|---|---|---|
   * | p50 sinuosity | 1.0178 | 1.0209 | 1.0228 | **1.0235** | 1.0238 | 1.0240 | 1.0242 |
   * | max excursion | 38.45 | 43.17 | 44.00 | **44.00** | 44.00 | 44.00 | 44.00 m |
   *
   * Turning it off entirely moves p50 sinuosity by 0.0007 and does not move
   * the excursion at all, so this was never the thing holding the line in —
   * the honest bound is the hard clamp below, and `--maxDeviation` is where
   * that is read. Kept nonzero because it is what stops a control point
   * drifting along a plateau where every other force is zero.
   */
  tether: number;
  /**
   * Weight on the canopy potential — metres of pull per unit of closure.
   *
   * Read against the vegetation `buildChunk` really grows, not against the
   * field itself. `tools/ways.ts --try=canopy=N --timber`, crowns the drawn
   * centreline passes under, per kilometre (the ruled line's rate is 11.19):
   *
   * | canopy | crowns/km | rungs at the excursion cap |
   * |---|---|---|
   * | 0 | 11.15 | 0.0% |
   * | 26 | 10.98 | 0.0% |
   * | 100 | 10.20 | 0.0% |
   * | **220** | **9.94** | **0.3%** |
   * | 450 | 9.61 | 5.3% |
   *
   * 220 is the knee: past it the term buys almost nothing more and starts
   * spending the route's length pressed against the clamp, which is a straight
   * line at a fixed offset — the exact failure this file exists to remove.
   *
   * The crowns column counts real plants out of `buildChunk`, so it is a
   * measurement of the route against the vegetation that exists on the day it
   * is taken and it moves when the plant kinds move. Re-run the sweep after
   * any change under `world/chunk.ts` rather than trusting this table; the
   * shape of it — a knee at 220 — is what the constant rests on.
   */
  canopy: number;
  /**
   * Weight on the wetness potential. Measured as the share of drawn rungs
   * standing on `log(1 + flow) ≥ 4.4`, by `tools/ways.ts --try=wet=N`: 9.1%
   * with the term off, then 8.0 / **7.4** / 7.3 / 7.2% at 34 / **100** / 200 /
   * 400. It saturates at 100 — below that there is boggy ground it has not yet
   * stepped off, above it there is only watercourse it has to cross whatever
   * the weight, and the extra weight is spent on the clamp instead (rungs
   * pressed against the cap go 0.3% → 1.1% → 1.6% over those last three).
   */
  wet: number;
  /**
   * Metres of central difference for `∇h`.
   *
   * A *scale selector*, not a precision knob: the relief a route answers to is
   * the 30–300 m relief, and a difference taken at ±3 m reads the 10 m octave's
   * own grain and jitters the line. **Half the control spacing**, and that is
   * the argument — at `grad = control` the difference straddles both
   * neighbouring control points, so a vertex is being pushed by relief the
   * control polygon cannot express and the two adjacent tension terms are
   * already answering for.
   *
   * `tools/ways.ts --grad`:
   *
   * | grad | 4 | 8 | 16 | 24 | 32 |
   * |---|---|---|---|---|---|
   * | p50 sinuosity | 1.0212 | 1.0235 | 1.0257 | 1.0269 | 1.0265 |
   * | total ascent | 2,786 | 2,779 | 2,767 | 2,764 | 2,764 m |
   *
   * **This sweep is not flat, and the shipped 12 is not its optimum.** An
   * earlier version of this note claimed both — "p50 sinuosity 1.0023 → 1.0033
   * across 4 → 32 m … nothing here is riding on this number" — and neither
   * figure nor the conclusion reproduces. What is true is the size: ascent
   * falls 22 m (0.8%) across an eightfold range and is flat from 16 on, and 12
   * sits at 2,772 m in the default run, 8 m of climb in 2,772 — 0.3% — above
   * the best value the sweep offers. That is what taking the scale argument
   * costs, stated rather than hidden, and it is available to anyone who thinks
   * the ascent is worth more than the scale.
   */
  grad: number;
  /**
   * Rounds of Chaikin corner-cutting, as `render/rivers.ts:107-119` does it.
   * Two, and for once that is not borrowed — `--rounds` measured the mean
   * movement per extra round at 6.30 / 1.54 / 0.39 / 0.10 m, so the corners are
   * off after two and the third round only shortens the line.
   */
  rounds: number;
  /**
   * Metres the drawn line may stray from the recorded one — rule 3's hard half.
   *
   * Set where the relaxation already stops rather than where it would be nice
   * to stop it, so it is a guarantee and not a shaper: `--maxDeviation` with
   * the cap removed entirely measures the free maximum excursion over all 33
   * ways at **48.6 m**, and p50 sinuosity (1.0235), p90 (1.0500) and total
   * ascent (2,771 m vs 2,772 m) are unchanged from the capped route. Halve it
   * to 22 m and it starts to bind — sinuosity falls to 1.0201 and the
   * wet-ground share rises from 7.4% to 7.5%; at 11 m, 1.0131 and 9.0%.
   *
   * **"Guarantee" is a claim about every record, not about this one.** Being
   * unbound on the shipped network is what makes 44 the right NUMBER; it is
   * `pullInside` that makes the cap hold at all, and neither this sweep nor
   * anything else measured on `public/prehistory.json` can check that, because
   * every shipped record is a straight line. `--bent` is the run that can:
   * over 24 configurations of bent record — up to sinuosity 2.24, at caps 11 m
   * and 44 m — worst excursion / cap is 1.0000 and never 1.0001, and 0 of
   * ~150,000 drawn rungs are outside. The clamp this replaced reached 2.52
   * (27.75 m under an 11 m cap) on the same table.
   */
  maxDeviation: number;
  /**
   * Metres of central difference for the cross-slope carried on `SeatedPoint`.
   * The ribbon is 2.4–7.6 m across, so ±3 m measures the slope over roughly the
   * width the value is going to be applied across, which is the only span at
   * which it is the right answer rather than an extrapolation.
   */
  crossEps: number;
  /**
   * Metres between ground samples along the finished line.
   *
   * 20 m was the old value and the ground does not honour it: `detailHeight`'s
   * band limit stops at `2 · LATTICE = 8 m` and its finest surviving octave is
   * a 10.0 m wave, so 20 m samples the relief a trail is meant to hug at a
   * quarter of the rate it needs.
   *
   * The criterion is not the raw gap — that falls smoothly to 4 m with no knee
   * in it — but whether the ground still cuts up *through* the ribbon, allowing
   * for the lift `render/paths.ts` already gives it (0.12 m + 1.4 mm per metre
   * of viewing distance, so 0.54 m at 300 m). `tools/ways.ts --steps`:
   *
   * | step | p95 gap | max gap | buried at 300 m | rungs |
   * |---|---|---|---|---|
   * | 20 m | 0.83 m | 2.80 m | 6.236% | 2,160 |
   * | 16 m | 0.66 m | 2.22 m | 4.007% | 2,690 |
   * | 12 m | 0.42 m | 1.39 m | 1.167% | 3,572 |
   * | 10 m | 0.31 m | 1.01 m | 0.375% | 4,278 |
   * | **8 m** | **0.22 m** | **0.66 m** | **0.005%** | **5,336** |
   * | 6 m | 0.13 m | 0.41 m | 0.000% | 7,103 |
   * | 4 m | 0.06 m | 0.21 m | 0.000% | 10,635 |
   *
   * 8 m is where the ground stops breaking the line at the altitude a way is
   * read from. 6 m buys the last two samples in forty thousand for a third more
   * vertices. Vertices are linear in `1/step` and are paid once per
   * `pathsVersion` change, not per frame.
   */
  step: number;
}

/**
 * Wetness normalisation, in `log(1 + flow)`.
 *
 * `render/rivers.ts` draws open water from `flow ≥ 620`, which is
 * `log 621 = 6.43`. The ground a walker avoids starts well below that — the
 * seep, the rushy hollow, the ground that is soft in spring — so the ramp
 * opens at 4.4 (`flow ≈ 80`, which is also where `world/chunk.ts`'s damp
 * margins begin) and saturates at 6.4, where there is standing water and the
 * question stops being "avoid" and becomes "ford". Beyond that the potential
 * is flat, so a way that has to cross a river still crosses it: the term can
 * choose *where* to cross, never whether.
 */
const WET_LO = 4.4, WET_HI = 6.4;

/**
 * Where the trees want to be — `world/chunk.ts`'s `standField`, mirrored.
 *
 * `render/farCanopy.ts` already keeps the same mirror and says why: it is a
 * pure fbm of world position, `chunk.ts` does not export it, and a renderer
 * that needs to know where the wood is has nowhere else to ask. This is the
 * second copy, and it is here rather than in `main.ts` so that wiring the
 * canopy term costs the caller one argument instead of five lines of noise.
 *
 * **If `chunk.ts` ever exports the real `standField`, delete this and pass
 * that instead** — the two are identical today and there is no test that can
 * notice when they stop being, because neither side can see the other.
 *
 * Deliberately NOT a licence to invent a field: the route may only bend around
 * things the world actually grows, and this is the world's own answer to where
 * it grows them.
 */
export const standClosure = (seed: number, x: number, z: number): number => {
  const stand = fbm(x / 1200, z / 1200, 3, seed + 401);
  const grain = fbm(x / 300, z / 300, 3, seed + 409);
  return clamp(smoothstep(0.36, 0.60, stand) * (0.32 + 0.85 * smoothstep(0.24, 0.58, grain)), 0, 1);
};

/**
 * The measured constants. Every number here came off `tools/ways.ts` against
 * the 33 ways in `public/prehistory.json`; the table each one was read from is
 * on its field above. What they add up to, before against after:
 *
 * | | ruled line at 20 m | this |
 * |---|---|---|
 * | sinuosity p10 / p50 / p90 / max | 1.0000 / 1.0000 / 1.0000 / 1.0000 | 1.0072 / 1.0235 / 1.0500 / 1.0581 |
 * | ground gap p95 / max | 0.70 m / 1.96 m | 0.22 m / 0.66 m |
 * | share of the line the ground cuts through at 300 m | 4.676% | 0.005% |
 * | grade p90, on a fixed 20 m base | 23.83% | 21.86% |
 * | total ascent, same base | 2,858 m | 2,772 m |
 * | rungs on wet ground | 9.5% | 7.4% |
 * | tree crowns walked under, per km | 11.19 | 9.94 |
 * | boles walked through, per km | 1.31 | 0.97 |
 * | max excursion from the record | 0.00 m | 44.00 m, the cap |
 * | rungs | 2,388 | 5,336 |
 * | build, whole network | 7 ms | 315 ms |
 *
 * The build cost is the one number here that is a real cost rather than a
 * measurement, and it is paid on `pathsVersion` — see `control`.
 *
 * **Re-run the tool rather than quoting this table.** Every row moves when a
 * constant moves, the two timber rows move when `world/chunk.ts` grows a new
 * kind of plant, and both build columns are wall-clock on one machine. Three
 * of the tables below were stale against their own named sweep before this was
 * written down.
 */
export const ROUTE: Readonly<RouteTune> = Object.freeze({
  control: 24,
  iterations: 24,
  rate: 1.6,
  settle: 0.97,
  climb: 22,
  tether: 0.03,
  canopy: 220,
  wet: 100,
  grad: 12,
  rounds: 2,
  maxDeviation: 44,
  crossEps: 3,
  step: 8,
});

/** Squared plan distance, which is all the inner loops ever need. */
const d2 = (ax: number, az: number, bx: number, bz: number): number =>
  (bx - ax) * (bx - ax) + (bz - az) * (bz - az);

/**
 * Plan distance — `sqrt(dx² + dz²)`, deliberately NOT `Math.hypot`.
 *
 * Rule 1 says two clients must draw the same line. IEEE-754 specifies `sqrt`
 * to be correctly rounded and specifies multiply and add exactly; it says
 * nothing at all about `Math.hypot`, whose accuracy is left to the engine, so
 * a hypot-shaped route is only reproducible on engines that happen to agree.
 * The overflow protection hypot buys is worth nothing here — these are metres
 * of island, not astronomical units — and this is also the faster of the two.
 */
const dist = (dx: number, dz: number): number => Math.sqrt(dx * dx + dz * dz);

/**
 * The point of a polyline nearest `(x, z)` in plan, and how far away it is.
 *
 * The FOOT, not just the length, because rule 3's clamp needs somewhere to pull
 * an escaped point back TO, and only one choice makes the cap exact — see
 * `pullInside`. Exported alongside `distanceToLine` so the probe measures the
 * same thing the renderer enforces rather than a second implementation of it.
 */
export function nearestOnLine(
  x: number, z: number, line: readonly RoutePoint[],
): { x: number; z: number; d: number } {
  let best = Infinity, bx = x, bz = z;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i], b = line[i + 1];
    const dx = b.x - a.x, dz = b.z - a.z;
    const len = dx * dx + dz * dz;
    const t = len < 1e-12 ? 0 : Math.min(1, Math.max(0, ((x - a.x) * dx + (z - a.z) * dz) / len));
    const cx = a.x + dx * t, cz = a.z + dz * t;
    const dd = d2(x, z, cx, cz);
    if (dd < best) { best = dd; bx = cx; bz = cz; }
  }
  if (line.length === 1) { best = d2(x, z, line[0].x, line[0].z); bx = line[0].x; bz = line[0].z; }
  return { x: bx, z: bz, d: Math.sqrt(best) };
}

/**
 * Distance from `(x, z)` to a polyline, in plan. Used for rule 3's hard clamp
 * and exported because the probe has to measure the same thing the renderer
 * enforces, or the two drift.
 */
export const distanceToLine = (
  x: number, z: number, line: readonly RoutePoint[],
): number => nearestOnLine(x, z, line).d;

/**
 * Pull a point back inside rule 3's tube: the set of positions within `cap`
 * metres of the recorded line.
 *
 * **Toward the nearest point OF the line, and that is the entire correctness
 * argument.** `c` is a point of the recorded polyline, so for the moved point
 * `p′ = c + (p − c)·cap/d` the distance to the whole polyline is at most the
 * distance to that one member of it: `distanceToLine(p′) ≤ |p′ − c| = cap`.
 * It holds for any shape of line, in one step, with no iteration.
 *
 * **What was here before did not hold, and the difference is the whole of this
 * function's existence.** The first cut scaled the displacement about the
 * ANCHOR — `p′ = r + (p − r)·cap/d` — which is exact only while the nearest
 * feature of the recorded line is a single straight segment through `r`. On a
 * bent line the nearest feature CHANGES as the point moves, and distance to a
 * polyline is not a convex function, so the scaled point could land further
 * out than the cap it was scaled by: measured on a 24-point zigzag under a 15 m
 * cap it drew to 19.90 m (+33%), and on a 90° hairpin under 20 m to 21.17 m.
 * That was latent only because every way in `public/prehistory.json` is
 * dead-straight (`tools/ways.ts` reports the RECORD's own sinuosity as 1.0000
 * at p10, p50, p90 and max), and on a straight line the old form was exact.
 * `test/ways.ts` asserts it on a bent record now, which the old clamp fails.
 *
 * `cap = Infinity` — what a probe measuring the unconstrained shape passes —
 * makes the comparison false and this a no-op, as does a NaN that should never
 * arrive.
 */
function pullInside(
  x: number, z: number, line: readonly RoutePoint[], cap: number,
): RoutePoint {
  const c = nearestOnLine(x, z, line);
  if (!(c.d > cap)) return { x, z };
  const f = cap / c.d;
  return { x: c.x + (x - c.x) * f, z: c.z + (z - c.z) * f };
}

/**
 * Resample a polyline at a fixed arc-length spacing.
 *
 * The first and last points come out **exactly** as they went in — that is
 * rule 2, and it is enforced here rather than trusted to the arithmetic,
 * because floating-point accumulation along 2 km of way would otherwise move
 * the last one by millimetres and a camp's road would stop meeting its camp.
 */
function resample(line: readonly RoutePoint[], spacing: number): RoutePoint[] {
  if (line.length < 2) return line.map((p) => ({ x: p.x, z: p.z }));
  const out: RoutePoint[] = [{ x: line[0].x, z: line[0].z }];
  let carry = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i], b = line[i + 1];
    const seg = dist(b.x - a.x, b.z - a.z);
    if (seg < 1e-9) continue;
    let at = spacing - carry;
    while (at < seg) {
      const f = at / seg;
      out.push({ x: a.x + (b.x - a.x) * f, z: a.z + (b.z - a.z) * f });
      at += spacing;
    }
    carry = seg - (at - spacing);
  }
  const end = line[line.length - 1];
  // the last emitted sample can land arbitrarily close to the end; drop it
  // rather than ship a rung of near-zero length, whose perpendicular is noise
  const last = out[out.length - 1];
  if (out.length > 1 && dist(end.x - last.x, end.z - last.z) < spacing * 0.35) out.pop();
  out.push({ x: end.x, z: end.z });
  return out;
}

/**
 * One round of Chaikin corner-cutting, endpoints preserved.
 * `render/rivers.ts:107-119` is the in-repo precedent and this is its shape:
 * a D8 river is right angles and a resampled way is a chain of chords, and in
 * both cases the corners are the generator showing through.
 */
function chaikin(line: readonly RoutePoint[]): RoutePoint[] {
  if (line.length < 3) return line.map((p) => ({ x: p.x, z: p.z }));
  const out: RoutePoint[] = [{ x: line[0].x, z: line[0].z }];
  for (let i = 0; i + 1 < line.length; i++) {
    const a = line[i], b = line[i + 1];
    out.push({ x: a.x * 0.75 + b.x * 0.25, z: a.z * 0.75 + b.z * 0.25 });
    out.push({ x: a.x * 0.25 + b.x * 0.75, z: a.z * 0.25 + b.z * 0.75 });
  }
  out.push({ x: line[line.length - 1].x, z: line[line.length - 1].z });
  return out;
}

/**
 * Settle the control polygon under the four forces. Pure, Jacobi, and the
 * endpoints are simply never in the loop.
 */
function relax(
  anchors: readonly RoutePoint[],
  recorded: readonly RoutePoint[],
  ground: (x: number, z: number) => number,
  fields: RouteFields | undefined,
  T: RouteTune,
): RoutePoint[] {
  const n = anchors.length;
  const pts = anchors.map((p) => ({ x: p.x, z: p.z }));
  if (n < 3) return pts;

  const canopy = fields?.canopy;
  const flow = fields?.flow;
  const wetness = flow
    ? (x: number, z: number) => smoothstep(WET_LO, WET_HI, Math.log(1 + Math.max(0, flow(x, z))))
    : undefined;

  const h = new Float64Array(n);
  const fx = new Float64Array(n);
  const fz = new Float64Array(n);
  const cap = T.control * 0.25;
  const e = T.grad;
  /**
   * The step shrinks geometrically each sweep (`rate *= settle`, below), and
   * that is what makes this *settle* rather than orbit.
   *
   * At a fixed step this is a limit cycle and not a convergence.
   * `tools/ways.ts --iterations --try=settle=1`, against `--iterations`:
   *
   * | sweeps | 5 | 10 | 20 | 40 | 80 | 160 |
   * |---|---|---|---|---|---|---|
   * | ascent, settle 1 | 2,810 | 2,792 | 2,776 | 2,774 | 2,786 | 2,787 m |
   * | mean shift, settle 1 | — | 7.01 | 8.60 | 9.29 | 8.73 | 7.59 m |
   * | ascent, settle 0.97 | 2,805 | 2,794 | 2,779 | 2,770 | 2,766 | 2,762 m |
   * | mean shift, settle 0.97 | — | 5.74 | 6.68 | 5.43 | 3.41 | 1.24 m |
   *
   * Undamped, total ascent — the thing being minimised — bottoms out at 40
   * sweeps and then climbs AGAIN, and the line never stops moving: 7.6 m of
   * mean shift per doubling at 160 sweeps, as much as at 10. A Jacobi update
   * with a step big enough to make progress is big enough to overshoot, and to
   * overshoot for ever. Damped, ascent falls monotonically and the shift decays
   * with it.
   *
   * The decay is GEOMETRIC rather than a ramp to zero over `iterations`
   * because the schedule has to be independent of the budget: annealed over
   * its own N, a 160-sweep run is a *different* schedule from an 80-sweep one,
   * so the two cannot be compared and `--iterations` stops being a convergence
   * test at all. `settle^it` sums to `rate/(1 − settle)` however many sweeps
   * are spent, so more sweeps only ever means more settled.
   *
   * Accumulated by repeated multiplication rather than `Math.pow(settle, it)`
   * for the same reason `dist` is not `Math.hypot`: IEEE-754 pins multiply
   * exactly and leaves `pow` to the engine.
   */
  let rate = T.rate;

  for (let it = 0; it < T.iterations; it++) {
    for (let i = 0; i < n; i++) h[i] = ground(pts[i].x, pts[i].z);
    for (let i = 1; i < n - 1; i++) {
      const p = pts[i], a = pts[i - 1], b = pts[i + 1];
      // ∇h at the vertex, central, at the scale the route answers to
      const gx = (ground(p.x + e, p.z) - ground(p.x - e, p.z)) / (2 * e);
      const gz = (ground(p.x, p.z + e) - ground(p.x, p.z - e)) / (2 * e);

      const l1 = Math.max(1e-6, dist(p.x - a.x, p.z - a.z));
      const l2 = Math.max(1e-6, dist(b.x - p.x, b.z - p.z));
      const u1x = (p.x - a.x) / l1, u1z = (p.z - a.z) / l1;
      const u2x = (b.x - p.x) / l2, u2z = (b.z - p.z) / l2;
      const dh1 = h[i] - h[i - 1], dh2 = h[i + 1] - h[i];
      const g1 = dh1 / l1, g2 = dh2 / l2;

      // Force is −∂E/∂p over the two adjacent segments, E = L + C·Δh²/L:
      //   ∂E/∂p = (1 − C·g1²)·u1 − (1 − C·g2²)·u2 + 2C·(g1 − g2)·∇h
      // so F = −t1·u1 + t2·u2 + 2C·(g2 − g1)·∇h.
      //
      // **The sign of the last term is the whole model and it was wrong once.**
      // Written as `−eq·∇h` the probe measured total ascent RISING from 2,967 m
      // to 4,460 m across the 33 ways — the relaxation was maximising the
      // difference between neighbouring grades, which is a staircase. With the
      // sign as derived above it falls instead. If a change here ever makes
      // `tools/ways.ts`'s ascent row go up, this is the line.
      const t1 = 1 - T.climb * g1 * g1;   // effective tension of the back segment
      const t2 = 1 - T.climb * g2 * g2;   // …and of the forward one
      let Fx = -t1 * u1x + t2 * u2x;
      let Fz = -t1 * u1z + t2 * u2z;
      // zero exactly when g1 === g2, which is why this settles into a
      // constant-grade traverse rather than a shortcut
      const eq = 2 * T.climb * (g2 - g1);
      Fx += eq * gx;
      Fz += eq * gz;

      if (canopy) {
        const cxp = canopy(p.x + e, p.z), cxm = canopy(p.x - e, p.z);
        const czp = canopy(p.x, p.z + e), czm = canopy(p.x, p.z - e);
        Fx -= T.canopy * (cxp - cxm) / (2 * e);
        Fz -= T.canopy * (czp - czm) / (2 * e);
      }
      if (wetness) {
        const wxp = wetness(p.x + e, p.z), wxm = wetness(p.x - e, p.z);
        const wzp = wetness(p.x, p.z + e), wzm = wetness(p.x, p.z - e);
        Fx -= T.wet * (wxp - wxm) / (2 * e);
        Fz -= T.wet * (wzp - wzm) / (2 * e);
      }

      const r = anchors[i];
      Fx -= 2 * T.tether * (p.x - r.x) / T.control;
      Fz -= 2 * T.tether * (p.z - r.z) / T.control;

      let mx = rate * Fx, mz = rate * Fz;
      const m = dist(mx, mz);
      if (m > cap) { mx = mx / m * cap; mz = mz / m * cap; }
      fx[i] = mx; fz[i] = mz;
    }
    rate *= T.settle;
    for (let i = 1; i < n - 1; i++) {
      // rule 3, hard: measured against the RECORDED line, not the anchors, so
      // the cap means what the contract says it means
      const q = pullInside(pts[i].x + fx[i], pts[i].z + fz[i], recorded, T.maxDeviation);
      pts[i].x = q.x; pts[i].z = q.z;
    }
  }
  return pts;
}

/**
 * Resample a recorded way into the line to draw.
 *
 * @param points  the way as the simulation recorded it, in order
 * @param ground  the drawn surface: `(x, z) => surfaceHeight(region, x, z, 1)`.
 *                Passed in rather than imported so this module stays free of
 *                the region and can be driven from a probe.
 */
export function routeFor(
  points: readonly RoutePoint[],
  ground: (x: number, z: number) => number,
  opts: RouteOptions,
): SeatedPoint[] {
  if (points.length === 0) return [];
  const T: RouteTune = { ...ROUTE, ...opts.tune };
  if (opts.maxDeviation !== undefined) T.maxDeviation = opts.maxDeviation;
  // a cap, not an override: see `RouteOptions.step`
  if (opts.step !== undefined) T.step = Math.min(opts.step, T.step);
  const step = Math.max(1, T.step);
  if (points.length === 1) {
    return [{ x: points[0].x, y: ground(points[0].x, points[0].z), z: points[0].z, cross: 0 }];
  }

  const anchors = resample(points, T.control);
  const settled = relax(anchors, points, ground, opts.fields, T);
  let curve: readonly RoutePoint[] = settled;
  for (let r = 0; r < T.rounds; r++) curve = chaikin(curve);
  const plan = resample(curve, step);

  /**
   * Rule 3 once more, on the line that is actually DRAWN.
   *
   * `relax` bounds the CONTROL points; what ships is those points after two
   * Chaikin rounds and an 8 m resample, and both of those are convex
   * combinations of points that are inside the tube. That is not enough: the
   * tube is a polyline dilated by a disc, which is not a convex set, so the
   * midpoint of two legal points can sit outside it wherever the recorded line
   * turns. The excess is small — a corner cut across a bend — but rule 3 is
   * stated as a guarantee about the drawn line and this is what makes it one.
   *
   * Free on everything shipped, and provably so: the 33 recorded ways are
   * straight, and a straight line's tube is a slab, which IS convex. A/B'd
   * against a copy of this module with the loop removed, over the 33 ways of
   * `public/prehistory.json`: **0 of the 5,336 drawn points move**, and
   * `tools/ways.ts` prints max excursion 44.00 m either way. It earns its
   * keep the day `sim/mortals/band.ts` records a bend, not today.
   *
   * The endpoints are skipped, which costs nothing — they are ON the recorded
   * line, at distance zero — and keeps rule 2 obviously untouched.
   */
  for (let i = 1; i < plan.length - 1; i++) {
    const q = pullInside(plan[i].x, plan[i].z, points, T.maxDeviation);
    plan[i].x = q.x; plan[i].z = q.z;
  }

  const out: SeatedPoint[] = [];
  const eps = T.crossEps;
  for (let i = 0; i < plan.length; i++) {
    const p = plan[i];
    // the ribbon's sideways axis, from the same averaged neighbours
    // `render/paths.ts` uses to mitre its rungs — so the cross-slope this
    // carries is the slope along the axis the ribbon is actually spread on
    const a = plan[Math.max(0, i - 1)], b = plan[Math.min(plan.length - 1, i + 1)];
    const dx = b.x - a.x, dz = b.z - a.z;
    const l = dist(dx, dz) || 1;
    const sx = -dz / l, sz = dx / l;
    const cross = (ground(p.x + sx * eps, p.z + sz * eps)
                 - ground(p.x - sx * eps, p.z - sz * eps)) / (2 * eps);
    out.push({ x: p.x, y: ground(p.x, p.z), z: p.z, cross });
  }
  return out;
}

/**
 * Sinuosity: walked length over straight-line distance between the ends.
 *
 * 1.0 is a ruled line. This is the number the ways work exists to move, and it
 * lives here rather than in a probe so that `tools/` and `test/` measure the
 * same function the renderer uses.
 */
export const sinuosity = (pts: readonly RoutePoint[]): number => {
  if (pts.length < 2) return 1;
  let walked = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    walked += dist(pts[i + 1].x - pts[i].x, pts[i + 1].z - pts[i].z);
  }
  const a = pts[0], b = pts[pts.length - 1];
  const direct = dist(b.x - a.x, b.z - a.z);
  return direct < 1e-6 ? 1 : walked / direct;
};
